import React, { useEffect, useState } from 'react';
//For amplitude analytics only
import { AMPLITUDE_API_KEY } from "../app/config";
import * as amplitude from '@amplitude/analytics-browser';
import { sendAnalyticsData } from '../app/log-interface';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import { plugin as engagementPlugin } from '@amplitude/engagement-browser';

import analConfig, { TILE_CATEGORIES } from '../app/analytics-config';
const isEnvLocal = window.location.host.search('localhost') > -1 ? true : false;
const { events } = analConfig;
//Restrict analytics for localhost
const analytics = {};

analytics.amplitude = amplitude;
let isAmplitudeInitialized = false;

/** enable on 26-feb */
const sessionReplayTracking = sessionReplayPlugin({
    sampleRate: 0.1
});
/** End enable on 26-feb */

// Store user info for Guides and Surveys
let currentUser = null;

// Internal state for base properties (property_name, property_id, selected_tile)
let _currentTile = null;
let _propertyName = null;
let _propertyId = null;

analytics.setState = function ({ currentTile, propertyName, propertyId }) {
    if (currentTile !== undefined) _currentTile = currentTile;
    if (propertyName !== undefined) _propertyName = propertyName;
    if (propertyId !== undefined) _propertyId = propertyId;
};

analytics.setTile = function (newTile) {
    _currentTile = newTile;
};

analytics.getBaseProps = function () {
    return {
        selected_tile: _currentTile,
        property_name: _propertyName,
        property_id: _propertyId,
        platform: 'web',
    };
};

analytics.setUserProperties = function (props) {
    if (isEnvLocal) return;
    const identifyEvent = new amplitude.Identify();
    Object.entries(props).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
            identifyEvent.set(key, value);
        }
    });
    amplitude.identify(identifyEvent);
};

// analytics.initialize = async (userInfo = null) => {
//     if(!isEnvLocal){
//     let userId = userInfo?.user_id;
//         amplitude.init(AMPLITUDE_API_KEY, userId,{
//             defaultTracking: {
//                 attribution: false,
//                 pageViews: false,
//                 sessions: true,
//                 formInteractions: false,
//                 fileDownloads: false,
//               },
//         });
//         amplitude.add(sessionReplayTracking);
//         amplitude.add(engagementPlugin());

//         // Boot Guides and Surveys with user information
//         if (userInfo) {
//             currentUser = userInfo;
//             // Add a small delay to ensure plugins are loaded
//             setTimeout(async () => {
//                 await analytics.bootGuidesAndSurveys(userInfo);
//             }, 100);
//         }
//     }
// }

analytics.initialize = async (userInfo = null) => {
    if (isEnvLocal) return;

    if (isAmplitudeInitialized) return;

    const userId = userInfo?.user_id;

    // ✅ Add plugins BEFORE init
    amplitude.add(sessionReplayTracking);
    amplitude.add(engagementPlugin());

    // ✅ Init
    amplitude.init(AMPLITUDE_API_KEY, userId, {
        defaultTracking: {
            attribution: false,
            pageViews: false,
            sessions: true,
            formInteractions: false,
            fileDownloads: false,
        },
    });

    isAmplitudeInitialized = true;

    if (userInfo) {
        currentUser = userInfo;
        await analytics.bootGuidesAndSurveys(userInfo);
    }
};

analytics.bootGuidesAndSurveys = async (userInfo) => {
    if (!isEnvLocal && window.engagement) {
        try {
            await window.engagement.boot({
                user: {
                    // Guides and Surveys requires at least one of user_id or device_id
                    user_id: userInfo.user_id || userInfo.id || userInfo.email,
                    device_id: userInfo.device_id || amplitude.getDeviceId(),
                    user_properties: userInfo.user_properties || {},
                },
                // Integration for tracking events to enable insights and responses
                integrations: [
                    {
                        track: (event) => {
                            analytics.track(event.event_type, event.event_properties);
                        }
                    },
                ],
            });
        } catch (error) {
            console.error('Failed to boot Guides and Surveys:', error);
        }
    }
}

analytics.guides = function () {
    // Remove duplicate plugin addition - it's already added in intilize()
    console.warn('guides() function is deprecated. Use bootGuidesAndSurveys() instead.');
}

analytics.track = function (eventKey, params, oldState, newState) {
    // Merge base props (property_name, property_id, selected_tile, platform) into every event;
    // caller-provided values always take precedence via spread order
    params = { ...analytics.getBaseProps(), ...(params || {}) };

    let eventName = eventKey;

    const configEntry = events[eventKey];
    if (configEntry) {
        eventName = configEntry.name;
        if (configEntry.category && !params.event_category) {
            params.event_category = configEntry.category;
        }
    }

    if (eventKey === "tile_change") {
        const tileId = params.selected_tile;
        if (tileId && tileId.includes(',')) {
            // Multi-tile (intellimerged) fired via tile_change
            eventName = 'Tile Intellimerged';
            params.event_category = 'Navigation';
        } else {
            // Single tile — look up display name and category from maps
            eventName = (analConfig.tiles && analConfig.tiles[tileId]) || tileId;
            params.event_category = TILE_CATEGORIES[tileId] || 'Tile';
        }
    }

    if (params.selected_tile) {
        let tiles = params.selected_tile.split(',');
        if (tiles.length > 1) {
            params.selected_tile = 'intellimerged tile';
        }
    }

    if (eventName) {
        //Insert into amplitude
        //Restrict analytics for localhost
        if (!isEnvLocal) {
            analytics.amplitude.track(eventName, params);

            // Forward events to Guides and Surveys for event-based triggers
            if (window.engagement) {
                window.engagement.forwardEvent({
                    event_type: eventName,
                    event_properties: params
                });
            }
        }
        //Insert into lodgiq system
        if (eventName !== "login attempt") {
            sendAnalyticsData(eventName, params, oldState, newState);
        }
    }
}

// Helper function to update user properties for Guides and Surveys
analytics.updateUserProperties = (userProperties) => {
    if (!isEnvLocal && window.engagement) {
        window.engagement._setUserProperties(userProperties);
    }
}

// Helper function to set session properties for Guides and Surveys
analytics.setSessionProperty = (key, value) => {
    if (!isEnvLocal && window.engagement) {
        window.engagement.setSessionProperty(key, value);
    }
}

// Helper function to show specific guide or survey
analytics.showGuide = (key, stepIndex = 0) => {
    if (!isEnvLocal && window.engagement) {
        window.engagement.gs.show(key, stepIndex);
    }
}

// Helper function to close all guides and surveys
analytics.closeAllGuides = () => {
    if (!isEnvLocal && window.engagement) {
        window.engagement.gs.closeAll();
    }
}

// Debug function to check Guides and Surveys status
analytics.debugGuidesAndSurveys = () => {
    if (!isEnvLocal && window.engagement) {
        console.log('Guides and Surveys Debug Status:', window.engagement._debugStatus());
        console.log('Available guides and surveys:', window.engagement.gs.list());
    } else {
        console.log('Guides and Surveys not available - check initialization');
    }
}

const useAnalytics = function (props) {
    const [analytics, setAnalytics] = useState({});

    useEffect(() => {

        setAnalytics(analytics);
    }, []);

    return analytics;
};
export { analytics };